import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatErrorMessage } from "src/utils/formatErrorMessage";

import { Bell02 } from "@sol/uikit/core/icons/Alerts/Bell02";
import { Badge } from "@sol/uikit/data-display/Badge/Badge";
import { Popover } from "@sol/uikit/data-display/Popover/Popover";
import { message } from "@sol/uikit/feedback/Message/Message";
import { Button } from "@sol/uikit/general/Button/Button";

import { NotificationPanel } from "./NotificationPanel";
import { getUnreadCountLabel } from "../lib/getUnreadCountLabel";
import { useNotificationMenu } from "../model/useNotificationMenu";

export const NotificationMenu = () => {
    const [t] = useTranslation();
    const [messageApi, contextHolder] = message.useMessage();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const bellRef = useRef<HTMLButtonElement>(null);

    const menu = useNotificationMenu({
        isOpen,
        onClose: () => setIsOpen(false),
        // React Query awaits onError before onSettled, and an AntD message only resolves once it closes
        onError: error => {
            messageApi.error(formatErrorMessage(error, t("component.ApiErrorAlert.defaultText")));
        },
    });

    const unreadCount = menu.unreadCount ?? 0;
    const unreadCountLabel = getUnreadCountLabel(unreadCount);

    // AntD popovers ignore Escape
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setIsOpen(false);
                bellRef.current?.focus();
            }
        };

        document.addEventListener("keydown", closeOnEscape);

        return () => document.removeEventListener("keydown", closeOnEscape);
    }, [isOpen]);

    return (
        <div ref={containerRef} className="relative ml-auto flex items-center">
            {contextHolder}
            <Popover
                open={isOpen}
                onOpenChange={setIsOpen}
                trigger="click"
                placement="bottomRight"
                arrow={false}
                destroyOnHidden
                // Rendered right after the bell so that Tab reaches the panel next
                getPopupContainer={() => containerRef.current ?? document.body}
                classNames={{ body: "overflow-hidden p-0!" }}
                content={
                    <NotificationPanel
                        notifications={menu.notifications}
                        isLoading={menu.isLoading}
                        hasLoadError={menu.hasLoadError}
                        hasNextPageError={menu.hasNextPageError}
                        onReload={menu.reload}
                        isReloading={menu.isReloading}
                        canFetchNextPage={menu.canFetchNextPage}
                        isFetchingNextPage={menu.isFetchingNextPage}
                        onReachEnd={menu.fetchNextPage}
                        hasUnread={menu.hasUnread}
                        isMarkingAllAsRead={menu.isMarkingAllAsRead}
                        onMarkAllAsRead={menu.markAllAsRead}
                        hasRead={menu.hasRead}
                        isArchivingRead={menu.isArchivingRead}
                        onArchiveRead={menu.archiveRead}
                        onOpenNotification={menu.openNotification}
                        onMarkNotificationAsRead={menu.markNotificationAsRead}
                        onArchiveNotification={menu.archiveNotification}
                    />
                }
            >
                <Badge
                    count={
                        unreadCount > 0 ? (
                            <span
                                className={clsx(
                                    "flex size-[22px] items-center justify-center rounded-full",
                                    "bg-(--ant-color-error) font-ant-strong text-white",
                                    "shadow-[0_0_0_1px_var(--ant-color-bg-container)]",
                                    unreadCountLabel.length > 2 ? "text-[9px]" : "text-[11px]",
                                )}
                            >
                                {unreadCountLabel}
                            </span>
                        ) : null
                    }
                >
                    <Button
                        ref={bellRef}
                        aria-label={
                            unreadCount > 0
                                ? t("navbar.openNotificationsWithUnread", { count: unreadCount })
                                : t("navbar.openNotifications")
                        }
                        aria-expanded={isOpen}
                        icon={<Bell02 size={20} className="text-color-ant-primary" />}
                        data-cy="notifications-button"
                    />
                </Badge>
            </Popover>
        </div>
    );
};
